import React, { useState, useEffect, useContext } from "react";
import { NavLink } from "react-router-dom";
import { AuthContext } from "../contexts/AuthContext";
import { fetchPermissions } from "../utils/checkPermissions";
import Spinner from "../Spinner";
import { useNavigate } from "react-router-dom";

// APPLICANT Sidebar Links
const APPLICANTNavbarLinks = [
  { resource: "profile", path: "/applicant/profile", title: "Profile" },
  { resource: "applications", path: "/applicant/applications", title: "Applications" },
  { resource: "interviews", path: "/applicant/interviews", title: "Interviews" },
  { resource: "messaging", path: "/applicant/messaging", title: "Messaging" },
  { resource: "settings", path: "/applicant/settings", title: "Settings" },
  { resource: "notifications", path: "/applicant/notifications", title: "Notifications" },
  { resource: "feedback", path: "/applicant/feedback", title: "Feedback" },
];

const DashboardSidebar = () => {
  const { user } = useContext(AuthContext);
  const [visibleRoutes, setVisibleRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loginType, setLoginType] = useState("job-seeker");
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  // Retrieve loginType from localStorage or context when Header is loaded
  useEffect(() => {
    const storedLoginType = localStorage.getItem("loginType");
    if (storedLoginType) {
      setLoginType(storedLoginType);
    }
  }, []);

  // Handle mouse hover to expand/collapse sidebar
  const handleMouseEnter = () => {
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    setIsOpen(false);
  };

  const setErrorMessage = (message) => {
    setError(message);
  };

  useEffect(() => {
    if (user) {
      const fetchPermissionsData = async () => {
        try {
          const validRoutes = [];
          for (const route of APPLICANTNavbarLinks) {
            // Check permissions for each route
            const hasPermission = await fetchPermissions(
              user.role,        // Assuming role is used for permission checks
              route.resource,   // Resource
              "view",            // Action
              loginType,       // loginType (assumed to be applicant here)
              setErrorMessage,   // Error setter
              navigate           // Navigate function to redirect after fetching
            );

            if (hasPermission) {
              validRoutes.push(route); // Only add valid routes to the list
            }
          }
          setVisibleRoutes(validRoutes); // Set the visible routes based on permission
        } catch (err) {
          setError("Failed to fetch permissions.");
        } finally {
          setLoading(false); // Stop loading once permissions are fetched
        }
      };

      fetchPermissionsData();
    } else {
      setLoading(false); // Stop loading if no user is found
    }
  }, [user, navigate]);

  return (
    <aside
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        width: isOpen ? "200px" : "60px", // Expand or collapse based on hover
        height: "calc(100vh - 60px)", // Start below the header (adjust `60px` to match header height)
        backgroundColor: "#f8f9fa",
        boxShadow: "2px 0 5px rgba(0, 0, 0, 0.1)",
        padding: isOpen ? "10px" : "5px", // Adjust padding for compact view
        position: "fixed",
        top: "60px", // Adjust `60px` to match the header height
        left: 0,
        zIndex: 1040, // Ensure sidebar appears above other elements
        transition: "width 0.3s ease, padding 0.3s ease", // Smooth transition
        overflow: "hidden", // Prevent content overflow
      }}
      aria-label="Dashboard Sidebar"
    >
      {error && <div style={{ color: "red" }}>{error}</div>}

      {loading ? (
        <Spinner /> // Show spinner instead of loading message
      ) : (
        <>
          <h3
            style={{
              marginBottom: "10px",
              color: "#333",
              textAlign: "center",
              display: isOpen ? "block" : "none", // Hide when sidebar is collapsed
              fontSize: "14px", // Smaller font size
            }}
          >
            Dashboard
          </h3>
          <ul
            style={{
              listStyleType: "none",
              padding: 0,
              margin: 0,
            }}
          >
            {visibleRoutes.map(({ path, title }) => (
              <li
                key={path}
                style={{
                  marginBottom: "5px", // Reduced spacing between items
                  textAlign: isOpen ? "left" : "center", // Center text for compact view
                }}
              >
                <NavLink
                  to={path}
                  style={({ isActive }) => ({
                    textDecoration: "none",
                    color: isActive ? "#fff" : "#007bff",
                    backgroundColor: isActive ? "#007bff" : "transparent",
                    padding: "5px", // Minimal padding for compact view
                    borderRadius: "5px",
                    display: "block",
                    fontWeight: "500",
                    fontSize: isOpen ? "12px" : "10px", // Adjust font size for compact view
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    transition: "background-color 0.3s ease, color 0.3s ease",
                  })}
                  title={title} // Tooltip for compact view
                >
                  {isOpen ? title : title.charAt(0)} {/* Full text or initials */}
                </NavLink>
              </li>
            ))}
          </ul>
        </>
      )}
    </aside>
  );
};

export default DashboardSidebar;
